export const waitForMs = (t) => {
  return new Promise((res) => {
    setTimeout(() => {
      res();
    }, t);
  });
};
export const { DEMO_URL, LIVE_URL, MD_URL, WS_DEMO_URL, WS_LIVE_URL } = {
  DEMO_URL: "https://demo.tradovateapi.com/v1",
  LIVE_URL: "https://live.tradovateapi.com/v1",
  MD_URL: "wss://md.tradovateapi.com/v1/websocket",
  WS_DEMO_URL: "wss://demo.tradovateapi.com/v1/websocket",
  WS_LIVE_URL: "wss://live.tradovateapi.com/v1/websocket",
};

const noop = () => {};

/**
 * Call to make GET requests to the Tradovate REST API. The passed `query` object will be reconstructed to a query string and placed in the query position of the URL.
 * ```js
 * //no parameters
 *  const jsonResponseA = await tvGet('/account/list')
 *
 * //parameter object, URL will become '/contract/item?id=2287764'
 * const jsonResponseB = await tvGet('/contract/item', { id: 2287764 })
 * ```
 *
 * New! You can interact with the browser devolopers' console. In the console enter commands:
 * ```
 * > tradovate.get('/account/list') //=> account data []
 * > tradovate.get('/contract/item', {id: 12345}) //=> maybe contract
 * ```
 *
 * @param {string} endpoint
 * @param {{[k:string]: any}} query object key-value-pairs will be converted into query, for ?masterid=1234 use `{masterid: 1234}`
 * @param {'demo' | 'live'} env
 * @returns
 */
export const tvGet = async (
  endpoint,
  query = null,
  env = "demo",
  token = ""
) => {
  try {
    let q = "";
    if (query) {
      q = Object.keys(query).reduce((acc, next, i, arr) => {
        acc += next + "=" + query[next];
        if (i !== arr.length - 1) acc += "&";
        return acc;
      }, "?");
    }

    console.log("With query:", q.toString() || "<no query>");

    let baseURL = env === "demo" ? DEMO_URL : env === "live" ? LIVE_URL : "";
    if (!baseURL)
      throw new Error(
        `[Services:tvGet] => 'env' variable should be either 'live' or 'demo'.`
      );

    let url = query !== null ? baseURL + endpoint + q : baseURL + endpoint;

    console.log(url);

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    const js = await res.json();

    return js;
  } catch (err) {
    console.error(err);
  }
};

/**
 * Use this function to make POST requests to the Tradovate REST API. `data` will be placed in the body of the request as JSON.
 * ```js
 * //placing an order with tvPost
 * const jsonResponseC = await tvPost('/order/placeorder', {
 *   accountSpec: myAcct.name,
 *   accountId: myAcct.id,
 *   action: 'Buy',
 *   symbol: 'MNQM1',
 *   orderQty: 2,
 *   orderType: 'Market',
 *   isAutomated: true //was this order placed by you or your robot?
 * })
 * ```
 *
 * @param {string} endpoint
 * @param {{[k:string]: any}} data
 * @param {boolean} _usetoken
 * @param {'live' | 'demo'} env
 * @returns
 */
export const tvPost = async (
  endpoint,
  data,
  _usetoken = true,
  env = "demo",
  token = ""
) => {
  const bearer = _usetoken ? { Authorization: `Bearer ${token}` } : {};

  let baseURL = env === "demo" ? DEMO_URL : env === "live" ? LIVE_URL : "";
  if (!baseURL)
    throw new Error(
      `[Services:tvPost] => 'env' variable should be either 'live' or 'demo'.`
    );

  try {
    const res = await fetch(baseURL + endpoint, {
      method: "POST",
      headers: {
        ...bearer,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const js = await res.json();

    return js;
  } catch (err) {
    console.error(err);
  }
};

//New! Interact with the API via browser console.
// window.tradovate = {
//     get: tvGet,
//     post: tvPost
// }

/**
 * A generic implementation for the Tradovate real-time APIs WebSocket client.
 *
 * ```js
 * const mySocket = new TradovateSocket()
 * //if you want labels for logs, do this
 * const myLabeledSocket = new TradovateSocket({debugLabel: 'my-websocket'})
 * ```
 */
export function TradovateSocket({
  debugLabel = "tvSocket",
  debug = false,
} = {}) {
  let counter = 1;
  let curTime = new Date();

  this.listeningURL = "";
  this.debugLabel = debugLabel;
  this.debug = debug;

  this.increment = function () {
    return counter++;
  };
  this.getCurTime = function () {
    return curTime;
  };
  this.setCurTime = function (t) {
    curTime = t === curTime ? curTime : t;
  };

  this.ws = null;
  this.connected = false;
  this.listeners = [];
  this.subscription_close_all = [];
  this.user_data = null;

  this.addListener = function (listener) {
    this.listeners.push(listener);
    return () => this.listeners.splice(this.listeners.indexOf(listener), 1);
  };
  this.add_subscription_closer = function (subscription_close) {
    this.subscription_close_all.push(subscription_close);
    return () =>
      this.subscription_close_all.splice(
        this.subscription_close_all.indexOf(subscription_close),
        1
      );
  };
}

/**
 * Get WSS url from HTTP tradovate given.
 *
 * ```
 * //market data connection example
 * const mySocket = new TradovateSocket()
 * mySocket.get_wss_url('https://demo.tradovateapi.com')
 * ```
 * @param {string} url The Tradovate HTTP URL.
 * @returns {string}
 */
TradovateSocket.prototype.get_wss_url = function (url) {
  if (`${url}/v1` == DEMO_URL) {
    url = WS_DEMO_URL;
  } else if (`${url}/v1` == LIVE_URL) {
    url = WS_LIVE_URL;
  } else {
    url = null;
  }
  if (this.debug) console.log("connecting to ", url);
  return url;
};

/**
 * Connect this client socket to one of the Tradovate real-time API URLs.
 *
 * ```js
 * //market data connection example
 * const mySocket = new TradovateSocket()
 * mySocket.connect('wss://md.tradovateapi.com/v1/websocket', MY_ACCESS_TOKEN)
 * ```
 * @param {string} url The Tradovate WebSocket URL to use for this client.
 * @param {string} token Your access token, acquired using the REST API.
 * @returns {Promise<void>}
 */
TradovateSocket.prototype.connect = async function (url, token) {
  if (this.debug) console.log("connecting...");
  const self = this;
  this.token = token;

  return new Promise((res, rej) => {
    this.listeningURL = url;
    this.ws = new WebSocket(url);

    //long running
    this.ws.addEventListener("message", function onEvents(msg) {
      self.setCurTime(checkHeartbeats(self.ws, self.getCurTime()));
      const [T, data] = prepareMessage(msg.data);

      if (T === "h") {
        if (self.debug) console.log(T);
      } else {
        if (self.debug) console.log(self.debugLabel + "\n", T, data);
      }

      if (T === "a" && data && data.length > 0) {
        self.listeners.forEach((listener) => data.forEach((d) => listener(d)));
      }
    });

    //auth only
    this.ws.addEventListener("message", function onConnect(msg) {
      const [T, _] = prepareMessage(msg.data);
      if (T === "o") {
        self.send({
          url: "authorize",
          body: token,
          onResponse: (_) => {
            self.ws.removeEventListener("message", onConnect);
            self.connected = true;
            res();
          },
          onReject: rej,
        });
      }
    });
  });
};

/**
 * Send a message via an authorized WebSocket. Parameters will depend on the request.
 * ```js
 * //accounts list example
 * const mySocket = new TradovateSocket()
 * await mySocket.connect('wss://demo.tradovateapi.com/v1/websocket')
 *
 * const [response] = await mySocket.send({url: 'account/list'})
 * console.log(response)
 * ```
 * @param {{url: string, query?: string, body?: { [k:string]: any }, onResponse?: (item: any) => void, onReject?: () => void} param0
 * @returns {Promise<{e?: string, d?: any, i: number, s: number}>}
 */
TradovateSocket.prototype.send = async function ({
  url,
  query,
  body,
  onResponse,
  onReject,
}) {
  const self = this;
  if (typeof query === "object") query = build_as_query_path_data(query);
  return new Promise((res, rej) => {
    const id = this.increment();
    self.ws.addEventListener("message", function onEvent(msg) {
      const [_, data] = prepareMessage(msg.data);
      data.forEach((item) => {
        if (item.s === 200 && item.i === id) {
          if (onResponse) {
            onResponse(item);
          }
          self.ws.removeEventListener("message", onEvent);
          res(item);
        } else if (item.s && item.s !== 200 && item.i && item.i === id) {
          if (this.debug) console.log("res err:", item);
          self.ws.removeEventListener("message", onEvent);
          if (onReject) {
            onReject(item);
          }
          rej(
            `\nFAILED:\n\toperation '${url}'\n\tquery ${
              query ? JSON.stringify(query, null, 2) : ""
            }\n\tbody ${body ? JSON.stringify(body, null, 2) : ""}\n\treason '${
              JSON.stringify(item?.d, null, 2) || "unknown"
            }'`
          );
        } else {
          if (self.debug) console.log("Unhandled send response:", id, item);
        }
      });
    });
    let sent = `${url}\n${id}\n${query || ""}\n${JSON.stringify(body)}`;
    if (this.debug) console.log("sending...", sent);
    this.ws.send(sent);
  });
};

/**
 * Creates a subscription to one of the real-time data endpoints. Returns a Promise of a function that when called cancels the subscription.
 * **Note: you can't cancel a user/syncrequest.**
 * ```js
 * const mySocket = new TradovateSocket()
 * await mySocket.connect('wss://demo.tradovateapi.com/v1/websocket')
 *
 * await mySocket.subscribe({
 *     url: 'user/syncrequest',
 *     body: { users: [123456] }, //user id
 *     subscription: console.log //log the data
 * })
 * ```
 * @param {{url: 'md/getchart' | 'md/subscribedom' | 'md/subscribequote' | 'md/subscribehistogram' | 'user/syncrequest' , body: {[k:string]: any}, subscription: (item: {[k: string]: any}) => void}} param0
 * @returns {Promise<() => void>}
 */
TradovateSocket.prototype.subscribe = async function ({
  url,
  body,
  subscription,
}) {
  const self = this;

  let removeListener = noop;
  let cancelUrl = "";
  let cancelBody = {};
  let contractId;

  let response = await this.send({ url, body });
  if (this.debug) console.log("MD", url, body, response);
  if (response?.d?.errorText) {
    throw new Error(response.d?.errorText);
  }
  if (response.d["p-ticket"]) {
    await waitForMs(response.d["p-time"] * 1000);
    let nextResponse = await self.send({
      url,
      body: { ...body, "p-ticket": response.d["p-ticket"] },
    });
    response = nextResponse;
  }
  if (this.debug) console.log("subs first response", response);
  const realtimeId = response?.d?.realtimeId || response?.d?.subscriptionId;
  if (body?.symbol) contractId = body.symbol;
  // if (body?.symbol && !body.symbol.startsWith("@")) {
  //   const contractRes = await tvGet(
  //     "/contract/find",
  //     { name: body.symbol },
  //     "demo",
  //     this.token
  //   );
  //   contractId = contractRes?.id || null;
  //   if (!contractId) {
  //     contractId = await tvGet(
  //       "/contract/suggest",
  //       { name: body.symbol },
  //       "demo",
  //       this.token
  //     )[0].id;
  //   }
  // }

  if (!realtimeId && response.d && response.d.users) {
    //for user sync request's initial response
    subscription(response.d);
  }

  return new Promise((res, rej) => {
    switch (url.toLowerCase()) {
      case "md/getchart": {
        cancelUrl = "md/cancelChart";
        cancelBody = { subscriptionId: realtimeId };
        if (this.listeningURL !== MD_URL)
          rej(
            "Cannot subscribe to Chart Data without using the Market Data URL."
          );
        removeListener = self.addListener((data) => {
          if (data.d.charts) {
            data.d.charts.forEach((chart) =>
              chart.id === realtimeId ? subscription(chart) : noop()
            );
          }
        });
        break;
      }
      case "md/subscribedom": {
        cancelUrl = "md/unsubscribedom";
        cancelBody = { symbol: body.symbol };
        if (this.listeningURL !== MD_URL)
          rej(
            "Cannot subscribe to DOM Data without using the Market Data URL."
          );
        removeListener = self.addListener((data) => {
          if (data.d.doms) {
            data.d.doms.forEach((dom) =>
              dom.contractId === contractId ? subscription(dom) : noop()
            );
          }
        });
        break;
      }
      case "md/subscribequote": {
        cancelUrl = "md/unsubscribequote";
        cancelBody = { symbol: body.symbol };
        if (this.listeningURL !== MD_URL)
          rej(
            "Cannot subscribe to Quote Data without using the Market Data URL."
          );
        removeListener = self.addListener((data) => {
          if (data.d.quotes?.length > 0) {
            data.d.quotes?.forEach((quote) => {
              quote.contractId === contractId ? subscription(quote) : noop();
            });
          }
        });
        break;
      }
      case "md/subscribehistogram": {
        cancelUrl = "md/unsubscribehistogram";
        cancelBody = { symbol: body.symbol };
        if (this.listeningURL !== MD_URL)
          rej(
            "Cannot subscribe to Histogram Data without using the Market Data URL."
          );
        removeListener = self.addListener((data) => {
          if (data.d.histograms) {
            data.d.histograms.forEach((histogram) =>
              histogram.contractId === contractId
                ? subscription(histogram)
                : noop()
            );
          }
        });
        break;
      }
      case "user/syncrequest": {
        if (
          this.listeningURL !== WS_DEMO_URL &&
          this.listeningURL !== WS_LIVE_URL
        )
          rej(
            "Cannot subscribe to User Data without using one of the Demo or Live URLs."
          );
        removeListener = self.addListener((data) => {
          if (data?.d?.users || data?.e === "props") {
            subscription(data.d);
          }
        });
        break;
      }
      default:
        rej("Incorrect URL parameters provided to subscribe.");
        break;
    }
    let subscription_close = async () => {
      if (self.debug) console.log("Closing subscription... ", cancelUrl);
      removeListener();
      if (cancelUrl && cancelUrl !== "") {
        await self.send({ url: cancelUrl, body: cancelBody });
      }
    };
    self.add_subscription_closer(subscription_close);
    res(subscription_close);
  });
};

TradovateSocket.prototype.close = async function () {
  if (this.debug) console.log("close ws...", this.gid);
  const self = this;
  self.subscription_close_all.forEach((s) => {
    try {
      s();
    } catch (error) {}
  });
  this.ws.close();
  this.connected = false;
  if (this.debug) console.log("closed", this.gid);
  return new Promise((res, rej) => {
    res(true);
  });
};

function checkHeartbeats(socket, curTime) {
  const now = new Date(); //time at call of onmessage

  if (now.getTime() - curTime.getTime() >= 2500) {
    socket.send("[]"); //send heartbeat
    return new Date(); //set the new base time
  }

  return curTime;
}

function prepareMessage(raw) {
  const T = raw.slice(0, 1);
  const data = raw.length > 1 ? JSON.parse(raw.slice(1)) : [];

  return [T, data];
}

function build_as_query_path_data(params) {
  const fetch_url = new URL("http://test");
  Object.keys(params).forEach((k) => {
    if (k in params && !(params[k] === "" || params[k] === null)) {
      fetch_url.searchParams.append(k, params[k]);
    }
  });
  let search_params = fetch_url.search.substring(1);
  return search_params;
}
